import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { translateLeadSetupError } from '@/help/leadAttendanceSimpleCopy';
import AttendanceFlowPanel from '@/components/attendance/AttendanceFlowPanel';
import EvolutionConnectModal from '@/components/integrations/EvolutionConnectModal';
import { ArrowRight, ArrowLeft, CheckCircle2, Circle } from 'lucide-react';
import OnboardingStepper from '@/components/onboarding/OnboardingStepper';

// 2026-07-06 — this used to hardcode only Claude/OpenAI here, so a tenant
// who'd already connected e.g. xAI/Grok in Integraciones never saw it as an
// option in onboarding. Both steps below now fetch the real provider catalog
// (same one Integraciones uses) instead of a fixed list, so this never goes
// stale again as new providers get added.
const ACTIVE_STATUSES = new Set([undefined, null, 'active']);

// Sprint 40 — these keys are the SAME keys used by OnboardingStepper /
// onboardingStages.js (stages 3-6 of the 7-stage shared journey). This
// screen used to run its own separate "Paso 1 de 4" stepper starting back
// at 1, which made completing OnboardingWizard.js feel like it dropped the
// tenant into an unrelated second product. Now both screens render the same
// <OnboardingStepper>, so this is just where the shared bar picks back up.
const STEPS = [
  { key: 'whatsapp', title: 'Conecta tu WhatsApp' },
  { key: 'ai', title: 'Activa tu asistente IA' },
  { key: 'assignment', title: 'Asigna vendedores' },
  { key: 'live_test', title: 'Prueba en vivo' },
];

export default function LeadAttendanceSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(() => parseInt(sessionStorage.getItem('lead_setup_step') || '0'));
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/lead-attendance/setup-status');
      setStatus(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { sessionStorage.setItem('lead_setup_step', String(step)); }, [step]);

  // Poll status every 5s on active step
  useEffect(() => {
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  if (loading) return <PantallaCargando />;

  // Sprint 40 — real per-step completion still comes from the backend
  // (status?.steps[key].status === 'ok'), same source of truth as before;
  // only the visual presentation moved to the shared stepper so it reads as
  // a continuation of OnboardingWizard.js rather than a second "Paso 1 de 4".
  // Negocio/Detalles/Sede always show complete here — reaching this screen
  // at all requires having finished OnboardingWizard.js first.
  const completedKeys = new Set([
    'negocio', 'detalles', 'sede',
    ...STEPS.filter(s => status?.steps?.[s.key]?.status === 'ok').map(s => s.key),
  ]);
  const clickableKeys = new Set(STEPS.map(s => s.key));
  const currentKey = STEPS[step]?.key;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="lead-attendance-setup">
      <h1 className="text-xl font-bold text-[#0F172A] mb-1">Pon en marcha la atención automática</h1>
      <p className="text-sm text-[#64748B] mb-4">Últimos pasos para que tus clientes reciban respuesta al instante</p>

      <div className="mb-6">
        <AttendanceFlowPanel
          flow={status?.flow}
          flowSteps={status?.flow_steps}
          planFeatures={status?.plan_features}
          onSaved={loadStatus}
        />
      </div>

      <OnboardingStepper
        currentKey={currentKey}
        completedKeys={completedKeys}
        clickableKeys={clickableKeys}
        onStepClick={(key) => {
          const idx = STEPS.findIndex(s => s.key === key);
          if (idx >= 0) setStep(idx);
        }}
      />

      {/* Step Content */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
        {step === 0 && <Step1WhatsApp status={status} onNext={() => setStep(1)} onRefresh={loadStatus} />}
        {step === 1 && <Step2AI status={status} onNext={() => setStep(2)} onBack={() => setStep(0)} onRefresh={loadStatus} navigate={navigate} />}
        {step === 2 && <Step3Assignment status={status} onNext={() => setStep(3)} onBack={() => setStep(1)} onRefresh={loadStatus} />}
        {step === 3 && <Step4LiveTest status={status} onBack={() => setStep(2)} navigate={navigate} />}
      </div>
    </div>
  );
}

function Step1WhatsApp({ status, onNext, onRefresh }) {
  const connected = status?.whatsapp_ready;
  const [showEvolutionModal, setShowEvolutionModal] = useState(false);
  const [waProviders, setWaProviders] = useState([]);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [credForm, setCredForm] = useState({});
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get('/tenant/integrations/providers').then(r => {
      const list = (r.data || []).filter(p => p.category === 'whatsapp' && ACTIVE_STATUSES.has(p.status));
      setWaProviders(list);
    }).catch(() => {});
  }, []);

  const selectProvider = (p) => {
    if (p.key === 'evolution_api') { setShowEvolutionModal(true); return; }
    setConnectingProvider(p);
    setCredForm({});
  };

  const connectWaProvider = async () => {
    const missing = (connectingProvider.credential_schema || []).filter(f => f.required && !credForm[f.key]?.trim());
    if (missing.length > 0) { toast.error('Completa todos los campos requeridos'); return; }
    setConnecting(true);
    try {
      await api.post('/tenant/integrations', {
        provider_key: connectingProvider.key,
        display_name: connectingProvider.name,
        credentials: credForm,
      });
      toast.success(`${connectingProvider.name} conectado`);
      setConnectingProvider(null);
      setCredForm({});
      onRefresh();
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
    setConnecting(false);
  };

  return (
    <div className="space-y-4" data-testid="lead-setup-step-1">
      <h2 className="text-base font-semibold text-[#0F172A]">Conecta el WhatsApp de tu negocio</h2>
      <p className="text-sm text-[#64748B]">Elige el proveedor de WhatsApp que ya usas (o Evolution si no tienes uno todavía)</p>
      {connected ? (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3" data-testid="lead-setup-wa-connected">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <div><p className="text-sm font-medium text-emerald-800">WhatsApp conectado</p><p className="text-xs text-emerald-600">Tu número está listo para recibir mensajes</p></div>
        </div>
      ) : connectingProvider ? (
        <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-testid="lead-setup-wa-cred-form">
          <p className="text-sm text-[#0F172A]">Credenciales de {connectingProvider.name}</p>
          {(connectingProvider.credential_schema || []).map(field => (
            <Input key={field.key} type={field.type === 'password' ? 'password' : 'text'}
              placeholder={field.placeholder || field.label} value={credForm[field.key] || ''}
              onChange={e => setCredForm(f => ({ ...f, [field.key]: e.target.value }))}
              data-testid={`lead-setup-wa-cred-${field.key}`} />
          ))}
          <div className="flex gap-2">
            <Button size="sm" onClick={connectWaProvider} disabled={connecting} className="bg-[#0F172A] text-white">
              {connecting ? 'Conectando...' : 'Conectar'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setConnectingProvider(null); setCredForm({}); }}>Cancelar</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">WhatsApp no está conectado</p>
            <p className="text-xs text-amber-600 mt-1">Elige tu proveedor abajo</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {waProviders.map(p => (
              <Button key={p.key} variant="outline" size="sm" onClick={() => selectProvider(p)} data-testid={`lead-setup-wa-provider-${p.key}`}>
                {p.name}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end"><Button onClick={onNext} disabled={!connected}>Continuar <ArrowRight className="w-4 h-4 ml-1" /></Button></div>

      <EvolutionConnectModal
        open={showEvolutionModal}
        onOpenChange={setShowEvolutionModal}
        onConnected={() => { setShowEvolutionModal(false); onRefresh(); }}
      />
    </div>
  );
}

function Step2AI({ status, onNext, onBack, onRefresh, navigate }) {
  const [assistantName, setAssistantName] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiProviders, setAiProviders] = useState([]);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [credForm, setCredForm] = useState({});
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get('/tenant/ai-training/profile').then(r => setAssistantName(r.data?.assistant_name || '')).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/tenant/integrations/providers').then(r => {
      const list = (r.data || []).filter(p => p.category === 'ai' && ACTIVE_STATUSES.has(p.status));
      setAiProviders(list);
    }).catch(() => {});
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/ai-training/profile', { assistant_name: assistantName, tone: 'friendly' });
      toast.success('Asistente configurado');
      onRefresh();
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const connectAiProvider = async () => {
    const missing = (connectingProvider.credential_schema || []).filter(f => f.required && !credForm[f.key]?.trim());
    if (missing.length > 0) { toast.error('Completa todos los campos requeridos'); return; }
    setConnecting(true);
    try {
      await api.post('/tenant/integrations', {
        provider_key: connectingProvider.key,
        display_name: connectingProvider.name,
        credentials: credForm,
        config: { ai_purposes: ['inbox_whatsapp', 'general_default'] },
      });
      toast.success(`${connectingProvider.name} conectado`);
      setConnectingProvider(null);
      setCredForm({});
      onRefresh();
      // 2026-07-06 — the platform owner explicitly asked for this: after
      // saving the key, offer Config IA directly instead of leaving it as a
      // barely-visible link at the bottom of the step.
      if (navigate && window.confirm('IA conectada. ¿Quieres ir ahora a Config IA para personalizar cómo responde?')) {
        navigate('/tenant/ai/config');
      }
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
    setConnecting(false);
  };

  const aiReady = status?.auto_reply_ready;
  const entitled = status?.auto_reply_entitled;

  return (
    <div className="space-y-4" data-testid="lead-setup-step-2">
      <h2 className="text-base font-semibold text-[#0F172A]">Configura quién responde primero</h2>
      <p className="text-sm text-[#64748B]">Tu asistente virtual saluda y atiende mientras tú o tu equipo toman el control cuando quieran</p>

      {!entitled && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 font-medium">Tu plan no incluye respuestas automáticas</p>
          <p className="text-xs text-amber-600 mt-1">Contacta a soporte para activarlo. Puedes continuar con los demás pasos.</p>
        </div>
      )}

      {!status?.ai_provider_connected && entitled && (
        <div className="space-y-3" data-testid="lead-setup-ai-connect">
          {connectingProvider ? (
            <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
              <p className="text-sm text-[#0F172A]">Credenciales de {connectingProvider.name}</p>
              {(connectingProvider.credential_schema || []).map(field => (
                <Input key={field.key} type={field.type === 'password' ? 'password' : 'text'}
                  placeholder={field.placeholder || field.label} value={credForm[field.key] || ''}
                  onChange={e => setCredForm(f => ({ ...f, [field.key]: e.target.value }))}
                  data-testid={`lead-setup-ai-cred-${field.key}`} />
              ))}
              <div className="flex gap-2">
                <Button size="sm" onClick={connectAiProvider} disabled={connecting} className="bg-[#0F172A] text-white">
                  {connecting ? 'Conectando...' : 'Conectar'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setConnectingProvider(null); setCredForm({}); }}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-[#64748B]">Elige con qué IA quieres que responda tu asistente</p>
              <div className="flex flex-wrap gap-2">
                {aiProviders.map(p => (
                  <Button key={p.key} variant="outline" size="sm" onClick={() => { setConnectingProvider(p); setCredForm({}); }} data-testid={`lead-setup-ai-provider-${p.key}`}>
                    {p.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {status?.ai_provider_connected && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#64748B]">Nombre de tu asistente</label>
            <input value={assistantName} onChange={e => setAssistantName(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Ej: Ana, Asistente de Mi Negocio" />
          </div>
          <Button onClick={saveProfile} disabled={saving || !assistantName.trim()} size="sm" className="bg-[#0F172A] text-white">
            {saving ? 'Guardando...' : 'Guardar asistente'}
          </Button>
        </div>
      )}

      {aiReady && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3" data-testid="lead-setup-ai-ready">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-800">Listo: la IA responderá automáticamente en WhatsApp</p>
        </div>
      )}

      <p className="text-sm text-[#475569]"><a href="/tenant/ai/config" className="underline font-medium text-[#0F172A]">Personalizar cómo responde la IA (Config IA)</a></p>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Atrás</Button>
        <Button onClick={onNext}>Continuar <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );
}

function Step3Assignment({ status, onNext, onBack, onRefresh }) {
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  // 2026-07-06 — this step used to only list whoever already existed on the
  // team (just the Owner on a brand new tenant), with no way to add anyone
  // without leaving the whole onboarding flow to go find Configuración >
  // Equipo. Now it can invite inline, reusing the same endpoint TeamManager
  // uses.
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', visible_role: 'Vendedor', delivery_method: 'copy_link' });
  const [inviting, setInviting] = useState(false);

  const reloadTeam = () => api.get('/tenant/business/team').then(r => setTeam(r.data || [])).catch(() => {});

  useEffect(() => { reloadTeam(); }, []);

  const toggleEligible = async (id, current) => {
    try {
      await api.patch(`/tenant/business/team/${id}`, { is_lead_eligible: !current });
      await reloadTeam();
      onRefresh();
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
  };

  const handleInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) { toast.error('Nombre y correo son requeridos'); return; }
    setInviting(true);
    try {
      await api.post('/tenant/business/team/invite', inviteForm);
      toast.success('Vendedor invitado');
      setShowInvite(false);
      setInviteForm({ name: '', email: '', visible_role: 'Vendedor', delivery_method: 'copy_link' });
      await reloadTeam();
      onRefresh();
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
    setInviting(false);
  };

  const activateAssignment = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/assignment/config', { is_enabled: true, strategy: 'round_robin', auto_on_inbound_convert: true });
      toast.success('Asignación automática activada');
      onRefresh();
    } catch (e) { toast.error(translateLeadSetupError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const ready = status?.assignment_ready;

  return (
    <div className="space-y-4" data-testid="lead-setup-step-3">
      <h2 className="text-base font-semibold text-[#0F172A]">¿Quién recibe los leads?</h2>
      <p className="text-sm text-[#64748B]">Cuando un cliente escribe, el sistema asigna a un vendedor disponible</p>

      <div className="space-y-2">
        {team.map(m => (
          <div key={m.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
            <div><p className="text-sm font-medium text-[#0F172A]">{m.name || m.email}</p><p className="text-xs text-[#94A3B8]">{m.role}</p></div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={m.is_lead_eligible} onChange={() => toggleEligible(m.id, m.is_lead_eligible)} className="rounded border-[#E2E8F0]" />
              <span className="text-xs text-[#64748B]">Recibe leads</span>
            </label>
          </div>
        ))}
        {team.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-4">Cargando equipo...</p>}
      </div>

      {showInvite ? (
        <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-testid="lead-setup-invite-form">
          <p className="text-sm text-[#0F172A]">Invitar vendedor</p>
          <Input placeholder="Nombre" value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} data-testid="lead-setup-invite-name" />
          <Input type="email" placeholder="Correo" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} data-testid="lead-setup-invite-email" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleInvite} disabled={inviting} className="bg-[#0F172A] text-white">
              {inviting ? 'Invitando...' : 'Invitar'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowInvite(false)}>Cancelar</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowInvite(true)} data-testid="lead-setup-invite-btn">
          + Invitar vendedor
        </Button>
      )}

      {!status?.assignment_enabled && (
        <Button onClick={activateAssignment} disabled={saving} className="w-full bg-[#0F172A] text-white" data-testid="lead-setup-assignment-ready">
          {saving ? 'Activando...' : 'Activar asignación automática'}
        </Button>
      )}

      {ready && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <p className="text-sm text-emerald-700">Asignación activa · {status.eligible_sellers_count} vendedores elegibles</p>
        </div>
      )}

      <p className="text-xs text-[#94A3B8]">Solo necesitas un vendedor para empezar — puedes invitar al resto de tu equipo cuando quieras.</p>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Atrás</Button>
        <Button onClick={onNext}>Continuar <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );
}

function Step4LiveTest({ status, onBack, navigate }) {
  const hasInbound = status?.recent_inbound_messages_1h >= 1;
  const hasAI = status?.recent_ai_replies_1h >= 1;
  const complete = status?.setup_complete;

  return (
    <div className="space-y-4" data-testid="lead-setup-step-4">
      <h2 className="text-base font-semibold text-[#0F172A]">Haz la prueba final</h2>
      <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
        <p className="text-sm text-[#0F172A] font-medium">Instrucciones:</p>
        <ol className="text-sm text-[#64748B] list-decimal list-inside space-y-1.5">
          <li>Desde <strong>otro teléfono</strong>, envía un mensaje al WhatsApp conectado</li>
          <li>En 10-15 segundos deberías ver la conversación en Conversaciones</li>
          <li>La IA debe responder sola (si tu plan lo incluye)</li>
          <li>El lead debe asignarse a un vendedor</li>
        </ol>
      </div>

      <div className="space-y-2">
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${hasInbound ? 'bg-emerald-50 border-emerald-200' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`}>
          {hasInbound ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-[#CBD5E1]" />}
          <span className="text-sm">{hasInbound ? 'Mensaje recibido' : 'Esperando mensaje de prueba...'}</span>
        </div>
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${hasAI ? 'bg-emerald-50 border-emerald-200' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`}>
          {hasAI ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-[#CBD5E1]" />}
          <span className="text-sm">{hasAI ? 'IA respondió' : 'Esperando respuesta automática...'}</span>
        </div>
      </div>

      {complete && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center" data-testid="lead-setup-complete">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-800">¡Todo listo! Tu negocio está atendiendo automáticamente</p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Atrás</Button>
        <Button onClick={() => navigate('/tenant/inbox')} className="bg-[#F72585] text-white">
          {complete ? 'Listo, empezar a vender' : 'Ir a Conversaciones'} <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
